import {storage, secureStorage} from './storage';
import {config} from '../constants';
import {ApiError} from '../types';
import {Platform} from 'react-native';
import {captureException} from './sentry';

export class NetworkError extends Error {
  constructor(message = 'No internet connection') {
    super(message);
    this.name = 'NetworkError';
  }
}

// Module-level network state tracking
let _isOnline = true;
const _listeners = new Set<(online: boolean) => void>();

export function getIsOnline(): boolean {
  return _isOnline;
}

export function onNetworkStatusChange(
  cb: (online: boolean) => void,
): () => void {
  _listeners.add(cb);
  return () => _listeners.delete(cb);
}

function setOnlineStatus(online: boolean) {
  if (_isOnline !== online) {
    _isOnline = online;
    _listeners.forEach(cb => cb(online));
  }
}

/**
 * Perform an actual connectivity check (not just read cached _isOnline).
 * On web: checks navigator.onLine (synchronous, instant)
 * On native: calls NetInfo.fetch() (~10ms) if available
 * Updates _isOnline so subsequent getIsOnline() calls benefit.
 */
export async function probeConnectivity(): Promise<boolean> {
  if (Platform.OS === 'web') {
    const online = typeof navigator !== 'undefined' ? navigator.onLine : true;
    setOnlineStatus(online);
    return online;
  }
  // Native: try NetInfo.fetch()
  try {
    const NetInfo = require('@react-native-community/netinfo').default;
    const state = await NetInfo.fetch();
    const online = state.isConnected ?? true;
    setOnlineStatus(online);
    return online;
  } catch {
    return _isOnline;
  }
}

// Auth invalidation listener (for 401 handling)
const _authListeners = new Set<() => void>();

export function onAuthInvalidated(cb: () => void): () => void {
  _authListeners.add(cb);
  return () => _authListeners.delete(cb);
}

function notifyAuthInvalidated() {
  _authListeners.forEach(cb => cb());
}

const REQUEST_TIMEOUT_MS = 15000;
const IMAGE_REQUEST_TIMEOUT_MS = 30000;

class ApiService {
  private baseUrl: string;

  constructor() {
    this.baseUrl = config.apiBaseUrl;
  }

  private async getToken(): Promise<string | null> {
    try {
      let token = await secureStorage.getItem(config.sessionTokenKey);
      // One-time migration: move token from AsyncStorage to secure storage
      if (!token && Platform.OS !== 'web') {
        token = await storage.getItem(config.sessionTokenKey);
        if (token) {
          await secureStorage.setItem(config.sessionTokenKey, token);
          await storage.removeItem(config.sessionTokenKey);
        }
      }
      return token;
    } catch {
      return null;
    }
  }

  async setToken(token: string): Promise<void> {
    await secureStorage.setItem(config.sessionTokenKey, token);
  }

  async clearToken(): Promise<void> {
    await secureStorage.removeItem(config.sessionTokenKey);
  }

  async hasToken(): Promise<boolean> {
    const token = await this.getToken();
    return token !== null;
  }

  private async request<T>(
    endpoint: string,
    options: RequestInit = {},
  ): Promise<T> {
    const token = await this.getToken();
    const headers: Record<string, string> = {
      'Content-Type': 'application/json',
    };

    if (token) {
      headers['Authorization'] = `Bearer ${token}`;
    }

    const controller = new AbortController();
    const timeoutId = setTimeout(() => controller.abort(), REQUEST_TIMEOUT_MS);

    let response: Response;
    try {
      response = await fetch(`${this.baseUrl}${endpoint}`, {
        ...options,
        headers,
        signal: controller.signal,
      });
    } catch (error: any) {
      clearTimeout(timeoutId);
      if (error?.name === 'AbortError') {
        setOnlineStatus(false);
        throw new NetworkError('Request timed out');
      }
      // fetch throws TypeError on network failure (no connection, DNS)
      setOnlineStatus(false);
      throw new NetworkError();
    }
    clearTimeout(timeoutId);

    // If we got a response, network is working
    setOnlineStatus(true);

    if (!response.ok) {
      if (response.status === 401) {
        notifyAuthInvalidated();
      }

      let errorData: ApiError;
      try {
        errorData = await response.json();
      } catch {
        errorData = {
          error: 'Error',
          message: `Request failed with status ${response.status}`,
        };
      }
      const apiError = new ApiServiceError(errorData.message, response.status, errorData);
      if (response.status >= 500) {
        captureException(apiError, {endpoint, status: response.status});
      }
      throw apiError;
    }

    // Handle empty responses
    const text = await response.text();
    if (!text) {
      return {} as T;
    }

    return JSON.parse(text);
  }

  async get<T>(endpoint: string): Promise<T> {
    return this.request<T>(endpoint, {method: 'GET'});
  }

  async post<T>(endpoint: string, data?: unknown): Promise<T> {
    return this.request<T>(endpoint, {
      method: 'POST',
      body: data ? JSON.stringify(data) : undefined,
    });
  }

  async patch<T>(endpoint: string, data: unknown): Promise<T> {
    return this.request<T>(endpoint, {
      method: 'PATCH',
      body: JSON.stringify(data),
    });
  }

  async put<T>(endpoint: string, data: unknown): Promise<T> {
    return this.request<T>(endpoint, {
      method: 'PUT',
      body: JSON.stringify(data),
    });
  }

  async delete<T>(endpoint: string): Promise<T> {
    return this.request<T>(endpoint, {method: 'DELETE'});
  }

  async getImageAsDataUrl(endpoint: string): Promise<string> {
    const token = await this.getToken();
    const headers: Record<string, string> = {};

    if (token) {
      headers['Authorization'] = `Bearer ${token}`;
    }

    const controller = new AbortController();
    const timeoutId = setTimeout(() => controller.abort(), IMAGE_REQUEST_TIMEOUT_MS);

    let response: Response;
    try {
      response = await fetch(`${this.baseUrl}${endpoint}`, {
        method: 'GET',
        headers,
        signal: controller.signal,
      });
    } catch (error: any) {
      clearTimeout(timeoutId);
      if (error?.name === 'AbortError') {
        setOnlineStatus(false);
        throw new NetworkError('Request timed out');
      }
      setOnlineStatus(false);
      throw new NetworkError();
    }
    clearTimeout(timeoutId);

    setOnlineStatus(true);

    if (!response.ok) {
      if (response.status === 401) {
        notifyAuthInvalidated();
      }
      throw new ApiServiceError(
        `Failed to fetch image: ${response.status}`,
        response.status,
        {error: 'Error', message: `Image fetch failed with status ${response.status}`},
      );
    }

    const blob = await response.blob();
    return new Promise((resolve, reject) => {
      const reader = new FileReader();
      reader.onloadend = () => resolve(reader.result as string);
      reader.onerror = reject;
      reader.readAsDataURL(blob);
    });
  }
}

export class ApiServiceError extends Error {
  constructor(
    message: string,
    public status: number,
    public data: ApiError,
  ) {
    super(message);
    this.name = 'ApiServiceError';
  }
}

export const api = new ApiService();
